import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  MenuCategoryBilling,
  MenuItemBilling,
  MenuCartItem,
  MenuCartTotals,
  MenuDailySummary,
  fetchMenuCategoriesBillingAPI,
  fetchMenuItemsBillingAPI,
  searchMenuItemsBillingAPI,
  placeMenuOrderAPI,
  fetchMenuDailySummaryAPI,
  CreateMenuOrder,
} from './menuBillingAPI';

// =============================================
// ASYNC THUNKS
// =============================================

export const fetchMenuBillingCategories = createAsyncThunk<
  MenuCategoryBilling[],
  void,
  { rejectValue: string }
>('menuBilling/fetchCategories', async (_, { rejectWithValue }) => {
  try {
    const result = await fetchMenuCategoriesBillingAPI();
    if (result.status && result.data) {
      return result.data;
    }
    return rejectWithValue(result.message || 'Failed to fetch menu categories');
  } catch (error) {
    return rejectWithValue('Failed to fetch menu categories');
  }
});

export const fetchMenuBillingItems = createAsyncThunk<
  { items: MenuItemBilling[]; meta: any },
  { categoryId?: string; search?: string; page?: number; limit?: number },
  { rejectValue: string }
>('menuBilling/fetchItems', async (params, { rejectWithValue }) => {
  try {
    const result = await fetchMenuItemsBillingAPI(params);
    if (result.status) {
      return {
        items: Array.isArray(result.data) ? result.data : [],
        meta: (result as any).meta || { total: 0, page: 1, limit: 50, totalPages: 0 },
      };
    }
    return rejectWithValue(result.message || 'Failed to fetch menu items');
  } catch (error) {
    return rejectWithValue('Failed to fetch menu items');
  }
});

export const searchMenuBillingItems = createAsyncThunk<
  MenuItemBilling[],
  { query: string; categoryId?: string },
  { rejectValue: string }
>('menuBilling/searchItems', async ({ query, categoryId }, { rejectWithValue }) => {
  try {
    const result = await searchMenuItemsBillingAPI(query, categoryId);
    if (result.status) {
      return Array.isArray(result.data) ? result.data : [];
    }
    return rejectWithValue(result.message || 'Search failed');
  } catch (error) {
    return rejectWithValue('Search failed');
  }
});

export const placeMenuOrder = createAsyncThunk<
  { bill: any; items: any[] },
  CreateMenuOrder,
  { rejectValue: string }
>('menuBilling/placeOrder', async (orderData, { rejectWithValue }) => {
  try {
    const result = await placeMenuOrderAPI(orderData);
    if (result.status) {
      return result.data!;
    }
    return rejectWithValue(result.message || 'Failed to place order');
  } catch (error) {
    return rejectWithValue('Failed to place order');
  }
});

export const fetchMenuBillingSummary = createAsyncThunk<
  MenuDailySummary,
  string | undefined,
  { rejectValue: string }
>('menuBilling/fetchDailySummary', async (date, { rejectWithValue }) => {
  try {
    const result = await fetchMenuDailySummaryAPI(date);
    if (result.status) {
      return result.data!;
    }
    return rejectWithValue(result.message || 'Failed to fetch summary');
  } catch (error) {
    return rejectWithValue('Failed to fetch daily summary');
  }
});

// =============================================
// HELPERS
// =============================================

const calculateCartTotals = (items: MenuCartItem[]): MenuCartTotals => {
  let subtotal = 0;
  const totalDiscount = 0;
  let totalTax = 0;
  let totalQuantity = 0;

  items.forEach(item => {
    const itemSubtotal = item.quantity * item.unitPrice;
    const taxAmount = (itemSubtotal * item.tax) / 100;

    subtotal += itemSubtotal;
    totalTax += taxAmount;
    totalQuantity += item.quantity;
  });

  subtotal = Math.round(subtotal * 100) / 100;
  totalTax = Math.round(totalTax * 100) / 100;
  const totalAmount = Math.round((subtotal - totalDiscount + totalTax) * 100) / 100;

  return { subtotal, totalDiscount, totalTax, totalAmount, totalQuantity };
};

// =============================================
// STATE
// =============================================

interface MenuBillingState {
  categories: MenuCategoryBilling[];
  categoriesLoading: boolean;
  categoriesError: string | null;
  selectedCategoryId: string | null;

  items: MenuItemBilling[];
  itemsLoading: boolean;
  itemsError: string | null;
  itemsMeta: { total: number; page: number; limit: number; totalPages: number };

  searchResults: MenuItemBilling[];
  searchLoading: boolean;
  searchQuery: string;

  cartItems: MenuCartItem[];
  totals: MenuCartTotals;

  orderLoading: boolean;
  orderError: string | null;
  lastOrder: { bill: any; items: any[] } | null;

  dailySummary: MenuDailySummary | null;
  summaryLoading: boolean;
}

const initialState: MenuBillingState = {
  categories: [],
  categoriesLoading: false,
  categoriesError: null,
  selectedCategoryId: null,

  items: [],
  itemsLoading: false,
  itemsError: null,
  itemsMeta: { total: 0, page: 1, limit: 50, totalPages: 0 },

  searchResults: [],
  searchLoading: false,
  searchQuery: '',

  cartItems: [],
  totals: { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 },

  orderLoading: false,
  orderError: null,
  lastOrder: null,

  dailySummary: null,
  summaryLoading: false,
};

// =============================================
// SLICE
// =============================================

const menuBillingSlice = createSlice({
  name: 'menuBilling',
  initialState,
  reducers: {
    setMenuBillingCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategoryId = action.payload;
    },

    setMenuBillingSearch: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },

    addToMenuCart: (state, action: PayloadAction<MenuItemBilling>) => {
      const item = action.payload;
      const existingIndex = state.cartItems.findIndex(ci => ci.menuItemId === item.uuid);

      if (existingIndex >= 0) {
        state.cartItems[existingIndex].quantity += 1;
      } else {
        state.cartItems.push({
          menuItemId: item.uuid,
          itemCode: item.item_code,
          itemName: item.item_name,
          quantity: 1,
          unitPrice: item.price || 0,
          originalPrice: item.price || 0,
          discount: 0,
          tax: 0,
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          image: item.image,
          description: item.description,
        });
      }

      state.totals = calculateCartTotals(state.cartItems);
    },

    removeFromMenuCart: (state, action: PayloadAction<string>) => {
      state.cartItems = state.cartItems.filter(ci => ci.menuItemId !== action.payload);
      state.totals = calculateCartTotals(state.cartItems);
    },

    updateMenuCartQuantity: (state, action: PayloadAction<{ itemId: string; quantity: number }>) => {
      const { itemId, quantity } = action.payload;
      const index = state.cartItems.findIndex(ci => ci.menuItemId === itemId);
      if (index >= 0) {
        if (quantity > 0) {
          state.cartItems[index].quantity = quantity;
        } else {
          state.cartItems.splice(index, 1);
        }
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    updateMenuCartPrice: (state, action: PayloadAction<{ itemId: string; unitPrice: number }>) => {
      const { itemId, unitPrice } = action.payload;
      const index = state.cartItems.findIndex(ci => ci.menuItemId === itemId);
      if (index >= 0 && unitPrice >= 0) {
        state.cartItems[index].unitPrice = unitPrice;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    resetMenuCartPrice: (state, action: PayloadAction<string>) => {
      const index = state.cartItems.findIndex(ci => ci.menuItemId === action.payload);
      if (index >= 0 && state.cartItems[index].originalPrice !== undefined) {
        state.cartItems[index].unitPrice = state.cartItems[index].originalPrice!;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    updateMenuCartDiscount: (state, action: PayloadAction<{ itemId: string; discount: number }>) => {
      const { itemId } = action.payload;
      const index = state.cartItems.findIndex(ci => ci.menuItemId === itemId);
      if (index >= 0) {
        state.cartItems[index].discount = 0;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    incrementMenuCartQty: (state, action: PayloadAction<string>) => {
      const index = state.cartItems.findIndex(ci => ci.menuItemId === action.payload);
      if (index >= 0) {
        state.cartItems[index].quantity += 1;
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    decrementMenuCartQty: (state, action: PayloadAction<string>) => {
      const index = state.cartItems.findIndex(ci => ci.menuItemId === action.payload);
      if (index >= 0) {
        if (state.cartItems[index].quantity > 1) {
          state.cartItems[index].quantity -= 1;
        } else {
          state.cartItems.splice(index, 1);
        }
        state.totals = calculateCartTotals(state.cartItems);
      }
    },

    clearMenuCart: (state) => {
      state.cartItems = [];
      state.totals = { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 };
    },

    resetAfterMenuOrder: (state) => {
      state.cartItems = [];
      state.totals = { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 };
      state.orderError = null;
      state.lastOrder = null;
    },
  },
  extraReducers: (builder) => {
    // Categories
    builder
      .addCase(fetchMenuBillingCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(fetchMenuBillingCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchMenuBillingCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload || 'Failed to fetch categories';
      });

    // Items
    builder
      .addCase(fetchMenuBillingItems.pending, (state) => {
        state.itemsLoading = true;
        state.itemsError = null;
      })
      .addCase(fetchMenuBillingItems.fulfilled, (state, action) => {
        state.itemsLoading = false;
        state.items = action.payload.items;
        state.itemsMeta = action.payload.meta;
      })
      .addCase(fetchMenuBillingItems.rejected, (state, action) => {
        state.itemsLoading = false;
        state.itemsError = action.payload || 'Failed to fetch items';
      });

    // Search
    builder
      .addCase(searchMenuBillingItems.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchMenuBillingItems.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchMenuBillingItems.rejected, (state) => {
        state.searchLoading = false;
        state.searchResults = [];
      });

    // Place Order
    builder
      .addCase(placeMenuOrder.pending, (state) => {
        state.orderLoading = true;
        state.orderError = null;
      })
      .addCase(placeMenuOrder.fulfilled, (state, action) => {
        state.orderLoading = false;
        state.lastOrder = action.payload;
        state.cartItems = [];
        state.totals = { subtotal: 0, totalDiscount: 0, totalTax: 0, totalAmount: 0, totalQuantity: 0 };
      })
      .addCase(placeMenuOrder.rejected, (state, action) => {
        state.orderLoading = false;
        state.orderError = action.payload || 'Failed to place order';
      });

    // Daily Summary
    builder
      .addCase(fetchMenuBillingSummary.pending, (state) => {
        state.summaryLoading = true;
      })
      .addCase(fetchMenuBillingSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.dailySummary = action.payload;
      })
      .addCase(fetchMenuBillingSummary.rejected, (state) => {
        state.summaryLoading = false;
      });
  },
});

export const {
  setMenuBillingCategory,
  setMenuBillingSearch,
  addToMenuCart,
  removeFromMenuCart,
  updateMenuCartQuantity,
  updateMenuCartPrice,
  resetMenuCartPrice,
  updateMenuCartDiscount,
  incrementMenuCartQty,
  decrementMenuCartQty,
  clearMenuCart,
  resetAfterMenuOrder,
} = menuBillingSlice.actions;

export default menuBillingSlice.reducer;
